var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import TurndownService from 'turndown';
var turndownService = new TurndownService({
    headingStyle: 'atx',
    bulletListMarker: '-',
    codeBlockStyle: 'fenced',
});
turndownService.remove(['script', 'style', 'meta']);
// Decode html but preserve tags: https://stackoverflow.com/a/7394787
var decodeHtml = function (html) {
    var txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
};
// Enhanced HTML detection that handles email client quirks
var isHtmlContent = function (text) {
    // Check for common HTML patterns from email clients
    var htmlPatterns = [
        /<[^>]+>/i, // Basic HTML tags
        /&[a-zA-Z0-9#]+;/i, // HTML entities
        /<br\s*\/?>/i, // Line breaks (common in email clients)
        /<p\s*[^>]*>/i, // Paragraph tags
        /<div\s*[^>]*>/i, // Div tags
        /<span\s*[^>]*>/i, // Span tags
    ];
    // If any HTML pattern is found, consider it HTML
    if (htmlPatterns.some(function (pattern) { return pattern.test(text); })) {
        return true;
    }
    // Fallback to DOMParser method for more complex cases
    try {
        var parser = new DOMParser();
        var doc = parser.parseFromString(text, 'text/html');
        var hasElementNodes = Array.from(doc.body.childNodes).some(function (_a) {
            var nodeType = _a.nodeType;
            return nodeType === 1;
        });
        return hasElementNodes;
    }
    catch (error) {
        // If parsing fails, assume it's plain text
        return false;
    }
};
/**
 * Converts API/calendar descriptions into markdown for RichTextInput on initial load.
 * Plain text and existing markdown pass through unchanged.
 */
export var normalizeDescriptionForEditor = function (text) {
    if (!(text === null || text === void 0 ? void 0 : text.length)) {
        return undefined;
    }
    var decoded = decodeHtml(text);
    if (!hasMeaningfulDescriptionText(decoded)) {
        return undefined;
    }
    if (!isHtmlContent(decoded)) {
        return decoded;
    }
    var markdown = turndownService.turndown(decoded).trim();
    return markdown || undefined;
};
/**
 * Returns true when an event description contains meaningful visible text.
 * This treats HTML-only wrappers (e.g. empty div/span/font markup and &nbsp;)
 * as empty content.
 */
export var hasMeaningfulDescriptionText = function (text) {
    if (!text || typeof text !== 'string') {
        return false;
    }
    try {
        var parser = new DOMParser();
        var doc = parser.parseFromString(text, 'text/html');
        var visibleText = (doc.body.textContent || '')
            .replace(/\u00a0/g, ' ')
            .replace(/\u200B/g, '')
            .trim();
        return visibleText.length > 0;
    }
    catch (error) {
        return text.replace(/\u00a0/g, ' ').trim().length > 0;
    }
};
/**
 * Splits an event description into the user-authored text and the integration
 * block that providers (e.g. Microsoft Teams) inject when a video conference is
 * added.
 *
 * We anchor on Microsoft's machine-generated wrapper (`class="me-email-text"`)
 * rather than the surrounding `----------------` fence lines, because users can
 * type their own dashes and we can't reliably tell those apart from the fence.
 * The injected block is always the trailing content, so everything before the
 * wrapper (minus the fence line that borders it) is the user's own text.
 *
 * @returns `userDescription` for display to the user, and `integrationHtml`
 *   (the raw injected block) or `null` when no integration block is present.
 */
export var splitEventDescription = function (raw) {
    if (!raw) {
        return { userDescription: '', integrationHtml: null };
    }
    var markerMatch = raw.match(/<div[^>]*class="[^"]*me-email-text[^"]*"[^>]*>/i);
    if (!markerMatch || markerMatch.index === undefined) {
        return { userDescription: raw, integrationHtml: null };
    }
    var integrationHtml = raw
        .slice(markerMatch.index)
        // Drop the closing fence that trails the injected block.
        .replace(/\s*-{4,}\s*$/, '')
        .trim();
    var userDescription = raw
        .slice(0, markerMatch.index)
        // Drop the opening fence that borders the injected block.
        .replace(/\s*-{4,}\s*$/, '')
        .trimEnd();
    return { userDescription: userDescription, integrationHtml: integrationHtml };
};
/**
 * Extracts the Meeting ID and Passcode from a Microsoft Teams integration
 * HTML block. Teams renders each as a `<span>` label ("Meeting ID:",
 * "Passcode:") immediately followed by a sibling `<span>` holding the value.
 *
 * Scoped to the already-isolated integration block (see
 * `splitEventDescription`) rather than the whole free-text description, so it
 * doesn't have to guess at arbitrary user content.
 */
export var parseTeamsMeetingDetails = function (html) {
    if (!html || typeof html !== 'string') {
        return {};
    }
    try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var spans_1 = Array.from(doc.querySelectorAll('span'));
        var valueForLabel = function (label) {
            var _a, _b;
            var labelSpan = spans_1.find(function (span) {
                return label.test(span.textContent || '');
            });
            var value = (_b = (_a = labelSpan === null || labelSpan === void 0 ? void 0 : labelSpan.nextElementSibling) === null || _a === void 0 ? void 0 : _a.textContent) === null || _b === void 0 ? void 0 : _b.replace(/\s+/g, ' ').trim();
            return value || undefined;
        };
        return {
            meetingId: valueForLabel(/meeting id/i),
            passcode: valueForLabel(/passcode/i),
        };
    }
    catch (error) {
        return {};
    }
};
// Recurrence parsing utilities
var DAY_ABBREVIATIONS = {
    MO: 'Monday',
    TU: 'Tuesday',
    WE: 'Wednesday',
    TH: 'Thursday',
    FR: 'Friday',
    SA: 'Saturday',
    SU: 'Sunday',
};
var MONTH_NAMES = {
    1: 'January',
    2: 'February',
    3: 'March',
    4: 'April',
    5: 'May',
    6: 'June',
    7: 'July',
    8: 'August',
    9: 'September',
    10: 'October',
    11: 'November',
    12: 'December',
};
var ORDINAL_SUFFIXES = {
    1: 'st',
    2: 'nd',
    3: 'rd',
    21: 'st',
    22: 'nd',
    23: 'rd',
    31: 'st',
};
var getOrdinalSuffix = function (num) {
    var numStr = num.toString();
    return ORDINAL_SUFFIXES[numStr] || 'th';
};
var formatDaysOfWeek = function (days) {
    if (days.length === 0)
        return '';
    if (days.length === 1)
        return days[0];
    if (days.length === 2)
        return "".concat(days[0], " and ").concat(days[1]);
    return "".concat(days.slice(0, -1).join(', '), ", and ").concat(days[days.length - 1]);
};
var parseByDay = function (byDay) {
    return byDay
        .split(',')
        .map(function (day) { return DAY_ABBREVIATIONS[day.trim()] || day.trim(); });
};
var parseByMonthDay = function (byMonthDay) {
    return byMonthDay.split(',').map(function (day) {
        var dayNum = parseInt(day.trim());
        return "".concat(dayNum).concat(getOrdinalSuffix(dayNum));
    });
};
var parseByMonth = function (byMonth) {
    return byMonth
        .split(',')
        .map(function (month) { return MONTH_NAMES[month.trim()] || month.trim(); });
};
var parseInterval = function (interval) {
    return parseInt(interval) || 1;
};
var parseCount = function (count) {
    return parseInt(count) || 0;
};
/**
 * Extracts the UNTIL end date from recurrence rules, if present.
 * Handles the YYYYMMDD and YYYYMMDDTHHMMSSZ RRULE formats. Uses the
 * date components as written in the rule (no timezone conversion),
 * since calendar providers set UNTIL to the end of the chosen day.
 */
export var getRecurrenceEndDate = function (rrules) {
    var e_1, _a, e_2, _b;
    if (!Array.isArray(rrules)) {
        return null;
    }
    try {
        for (var rrules_1 = __values(rrules), rrules_1_1 = rrules_1.next(); !rrules_1_1.done; rrules_1_1 = rrules_1.next()) {
            var rrule = rrules_1_1.value;
            if (!rrule || typeof rrule !== 'string')
                continue;
            var cleanRrule = rrule.startsWith('RRULE:') ? rrule.substring(6) : rrule;
            try {
                for (var _c = (e_2 = void 0, __values(cleanRrule.split(';'))), _d = _c.next(); !_d.done; _d = _c.next()) {
                    var part = _d.value;
                    var _e = __read(part.split('='), 2), key = _e[0], value = _e[1];
                    if ((key === null || key === void 0 ? void 0 : key.toUpperCase()) !== 'UNTIL' || !value)
                        continue;
                    var match = /^(\d{4})(\d{2})(\d{2})(?:T\d{6}Z)?$/.exec(value);
                    if (match) {
                        var _f = __read(match, 4), year = _f[1], month = _f[2], day = _f[3];
                        return new Date(Number(year), Number(month) - 1, Number(day));
                    }
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (rrules_1_1 && !rrules_1_1.done && (_a = rrules_1.return)) _a.call(rrules_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return null;
};
/**
 * Parses RRULE (Recurrence Rule) strings and converts them to human-readable text.
 *
 * Supports common RRULE patterns:
 * - FREQ (DAILY, WEEKLY, MONTHLY, YEARLY)
 * - INTERVAL (repetition interval)
 * - BYDAY (days of week)
 * - BYMONTHDAY (days of month)
 * - BYMONTH (months of year)
 * - COUNT (number of occurrences)
 * - BYSETPOS (position in set)
 *
 * The UNTIL end date is not part of the description — use
 * getRecurrenceEndDate to display it separately.
 *
 * @param rrule - The RRULE string to parse
 * @returns Human-readable recurrence description
 */
export var parseRecurrenceRule = function (rrule) {
    var _a;
    if (!rrule || typeof rrule !== 'string') {
        return '';
    }
    // Remove RRULE: prefix if present
    var cleanRrule = rrule.startsWith('RRULE:') ? rrule.substring(6) : rrule;
    // Parse RRULE parameters
    var params = {};
    var parts = cleanRrule.split(';');
    parts.forEach(function (part) {
        var _a = __read(part.split('='), 2), key = _a[0], value = _a[1];
        if (key && value) {
            params[key.toUpperCase()] = value;
        }
    });
    var freq = (_a = params.FREQ) === null || _a === void 0 ? void 0 : _a.toUpperCase();
    var interval = parseInterval(params.INTERVAL || '1');
    var byDay = params.BYDAY ? parseByDay(params.BYDAY) : [];
    var byMonthDay = params.BYMONTHDAY
        ? parseByMonthDay(params.BYMONTHDAY)
        : [];
    var byMonth = params.BYMONTH ? parseByMonth(params.BYMONTH) : [];
    var count = parseCount(params.COUNT || '0');
    var bySetPos = params.BYSETPOS ? parseInt(params.BYSETPOS) : 0;
    // Build the recurrence description
    var description = '';
    // Handle frequency and interval
    switch (freq) {
        case 'DAILY':
            if (interval === 1) {
                description = 'Every day';
            }
            else if (interval === 2) {
                description = 'Every other day';
            }
            else {
                description = "Every ".concat(interval, " days");
            }
            break;
        case 'WEEKLY':
            if (interval === 1) {
                if (byDay.length > 0) {
                    var daysText = formatDaysOfWeek(byDay);
                    description = "Every week on ".concat(daysText);
                }
                else {
                    description = 'Every week';
                }
            }
            else {
                if (byDay.length > 0) {
                    var daysText = formatDaysOfWeek(byDay);
                    description = "Every ".concat(interval, " weeks on ").concat(daysText);
                }
                else {
                    description = "Every ".concat(interval, " weeks");
                }
            }
            break;
        case 'MONTHLY':
            if (interval === 1) {
                if (byMonthDay.length > 0) {
                    var daysText = formatDaysOfWeek(byMonthDay);
                    description = "Every month on the ".concat(daysText);
                }
                else if (byDay.length > 0 && bySetPos !== 0) {
                    var daysText = formatDaysOfWeek(byDay);
                    var ordinal = "".concat(bySetPos).concat(getOrdinalSuffix(bySetPos));
                    description = "Every month on the ".concat(ordinal, " ").concat(daysText);
                }
                else {
                    description = 'Every month';
                }
            }
            else {
                if (byMonthDay.length > 0) {
                    var daysText = formatDaysOfWeek(byMonthDay);
                    description = "Every ".concat(interval, " months on the ").concat(daysText);
                }
                else {
                    description = "Every ".concat(interval, " months");
                }
            }
            break;
        case 'YEARLY':
            if (interval === 1) {
                if (byMonth.length > 0 && byMonthDay.length > 0) {
                    var monthsText = formatDaysOfWeek(byMonth);
                    var daysText = formatDaysOfWeek(byMonthDay);
                    description = "Every year on ".concat(monthsText, " ").concat(daysText);
                }
                else if (byMonth.length > 0) {
                    var monthsText = formatDaysOfWeek(byMonth);
                    description = "Every year in ".concat(monthsText);
                }
                else if (byDay.length > 0 && bySetPos !== 0) {
                    var daysText = formatDaysOfWeek(byDay);
                    var ordinal = "".concat(bySetPos).concat(getOrdinalSuffix(bySetPos));
                    description = "Every year on the ".concat(ordinal, " ").concat(daysText);
                }
                else {
                    description = 'Every year';
                }
            }
            else {
                if (byMonth.length > 0) {
                    var monthsText = formatDaysOfWeek(byMonth);
                    description = "Every ".concat(interval, " years in ").concat(monthsText);
                }
                else {
                    description = "Every ".concat(interval, " years");
                }
            }
            break;
        default:
            return 'Custom recurrence';
    }
    // Add end conditions. The UNTIL end date is intentionally not included
    // here — it renders as its own "Ends ..." line (see RecurrenceText).
    if (count > 0) {
        description += " (".concat(count, " times)");
    }
    return description;
};
/**
 * Parses multiple recurrence rules and returns a combined description.
 * Useful when an event has multiple RRULE strings.
 *
 * @param rrules - Array of RRULE strings
 * @returns Combined human-readable recurrence description
 */
export var parseMultipleRecurrenceRules = function (rrules) {
    if (!Array.isArray(rrules) || rrules.length === 0) {
        return '';
    }
    var descriptions = rrules
        .map(function (rule) { return parseRecurrenceRule(rule); })
        .filter(function (desc) { return desc.length > 0; });
    if (descriptions.length === 0) {
        return '';
    }
    if (descriptions.length === 1) {
        return descriptions[0];
    }
    return descriptions.join('; ');
};
